<script setup>
/* eslint-disable no-underscore-dangle */
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  showPlaceholder: { type: Boolean, default: false },
  lockKeys: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function makeEmptyRow() {
  return { label: '', key: '', placeholder: '', _keyAutoGenerated: true };
}

function fromModelValue(val) {
  const rows = (val || []).map(o => ({
    label: o.label || '',
    key: o.key || '',
    placeholder: o.placeholder || '',
    _keyAutoGenerated: false,
  }));
  rows.push(makeEmptyRow());
  return rows;
}

const rows = ref(fromModelValue(props.modelValue));
let emitting = false;

// Quando o pai envia novos valores (ex: ao abrir edição), sincroniza rows
watch(
  () => props.modelValue,
  val => {
    if (emitting) return;
    rows.value = fromModelValue(val);
  },
  { deep: true }
);

function emitUpdate() {
  emitting = true;
  const filled = rows.value.filter(r => r.label.trim() && r.key.trim());
  emit(
    'update:modelValue',
    filled.map(r => ({
      label: r.label.trim(),
      key: r.key.trim(),
      ...(r.placeholder ? { placeholder: r.placeholder.trim() } : {}),
    }))
  );
  setTimeout(() => {
    emitting = false;
  }, 0);
}

function isDuplicateLabel(index) {
  const val = rows.value[index].label.trim().toLowerCase();
  if (!val) return false;
  return rows.value.some(
    (r, i) => i !== index && r.label.trim().toLowerCase() === val
  );
}

function isDuplicateKey(index) {
  const val = rows.value[index].key.trim();
  if (!val) return false;
  return rows.value.some((r, i) => i !== index && r.key.trim() === val);
}

const errors = computed(() =>
  rows.value.map((_, i) => ({
    label: isDuplicateLabel(i),
    key: isDuplicateKey(i),
  }))
);

function ensureEmptyRow() {
  const last = rows.value[rows.value.length - 1];
  if (last.label || last.key) {
    rows.value.push(makeEmptyRow());
  }
}

function onLabelInput(index) {
  if (rows.value[index]._keyAutoGenerated !== false) {
    rows.value[index].key = toSlug(rows.value[index].label);
  }
  ensureEmptyRow();
  emitUpdate();
}

function onKeyInput(index) {
  rows.value[index]._keyAutoGenerated = false;
  ensureEmptyRow();
  emitUpdate();
}

function onPlaceholderInput() {
  emitUpdate();
}

function onLabelEnter(index, event) {
  event.preventDefault();
  ensureEmptyRow();
  const nextIndex = index + 1;
  setTimeout(() => {
    const inputs = document.querySelectorAll('.option-list-label-input');
    if (inputs[nextIndex]) inputs[nextIndex].focus();
  }, 0);
}

function removeRow(index) {
  const row = rows.value[index];
  if (row._keyAutoGenerated === false) {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      t('ATTRIBUTES_MGMT.OPTION_REMOVE_CONFIRM')
    );
    if (!confirmed) return;
  }
  if (rows.value.length === 1) {
    rows.value = [makeEmptyRow()];
  } else {
    rows.value.splice(index, 1);
    if (!rows.value.some(r => !r.label && !r.key)) {
      rows.value.push(makeEmptyRow());
    }
  }
  emitUpdate();
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Header -->
    <div
      class="grid gap-2 px-2 pb-1"
      :class="
        showPlaceholder
          ? 'grid-cols-[1fr_1fr_1fr_20px]'
          : 'grid-cols-[1fr_1fr_20px]'
      "
    >
      <span class="text-xs font-medium text-n-slate-10">{{
        t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.NAME_HEADER')
      }}</span>
      <span class="text-xs font-medium text-n-slate-10">{{
        t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.KEY_HEADER')
      }}</span>
      <span
        v-if="showPlaceholder"
        class="text-xs font-medium text-n-slate-10"
        >{{ t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.PLACEHOLDER_HEADER') }}</span
      >
      <span />
    </div>

    <!-- Linhas -->
    <div
      v-for="(row, index) in rows"
      :key="index"
      class="flex flex-col gap-0.5"
    >
      <div
        class="grid gap-2 items-center px-2"
        :class="
          showPlaceholder
            ? 'grid-cols-[1fr_1fr_1fr_20px]'
            : 'grid-cols-[1fr_1fr_20px]'
        "
      >
        <input
          v-model="row.label"
          class="option-list-label-input w-full text-sm bg-n-surface-2 border rounded-lg px-2.5 py-1.5 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none"
          :class="errors[index].label ? 'border-n-ruby-9' : 'border-n-weak'"
          :placeholder="t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.NAME_PLACEHOLDER')"
          @input="onLabelInput(index)"
          @keydown.enter="onLabelEnter(index, $event)"
        />
        <input
          v-model="row.key"
          class="w-full text-sm bg-n-surface-2 border rounded-lg px-2.5 py-1.5 font-mono placeholder:text-n-slate-9 focus:outline-none"
          :class="[
            errors[index].key ? 'border-n-ruby-9' : 'border-n-weak',
            lockKeys && !row._keyAutoGenerated
              ? 'text-n-slate-9 cursor-not-allowed'
              : 'text-n-slate-10',
          ]"
          :readonly="lockKeys && !row._keyAutoGenerated"
          :placeholder="t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.KEY_PLACEHOLDER')"
          @input="onKeyInput(index)"
        />
        <input
          v-if="showPlaceholder"
          v-model="row.placeholder"
          class="w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none"
          :placeholder="
            t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.PLACEHOLDER_PLACEHOLDER')
          "
          @input="onPlaceholderInput()"
        />
        <button
          type="button"
          class="flex items-center justify-center text-n-slate-9 hover:text-n-ruby-9 transition-colors"
          :class="{ 'opacity-0 pointer-events-none': !row.label && !row.key }"
          @click="removeRow(index)"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>
      <div v-if="errors[index].label || errors[index].key" class="px-2">
        <p v-if="errors[index].label" class="text-xs text-n-ruby-9">
          {{ t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.DUPLICATE_NAME') }}
        </p>
        <p v-if="errors[index].key" class="text-xs text-n-ruby-9">
          {{ t('ATTRIBUTES_MGMT.ADD.FORM.OPTIONS.DUPLICATE_KEY') }}
        </p>
      </div>
    </div>
  </div>
</template>
